import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';

export interface TeacherCardData {
  id: string;
  name: string;
  image: string;
  speciality: string;
  biography?: string;
  /** Extra subjects beyond the primary badge (shows «+N مواد») */
  extraSubjectsCount?: number;
}

interface TeacherCardProps {
  teacher: TeacherCardData;
  onClick: (teacherId: string) => void;
  compact?: boolean;
  /** Base path for teacher profile link — default `/teacher/` */
  teacherPathPrefix?: string;
}

const TeacherCard: React.FC<TeacherCardProps> = ({
  teacher,
  onClick,
  teacherPathPrefix = '/teacher/',
}) => {
  const titleId = `teacher-card-title-${teacher.id}`;
  const bio = teacher.biography?.trim() || teacher.speciality;
  const prefix = teacherPathPrefix.endsWith('/') ? teacherPathPrefix : `${teacherPathPrefix}/`;
  const href = `${prefix}${teacher.id}`;

  return (
    <article
      role="listitem"
      className={`group flex h-full w-full min-w-0 cursor-pointer flex-col ${cardTheme.courseCard} rounded-2xl`}
      aria-labelledby={titleId}
      onClick={() => onClick(teacher.id)}
    >
      {/* Portrait — fixed 3:4 so faces stay framed */}
      <div className="relative aspect-[3/4] w-full shrink-0 overflow-hidden bg-green-50">
        <img
          src={teacher.image}
          alt={`صورة ${teacher.name}`}
          width={360}
          height={480}
          loading="lazy"
          decoding="async"
          sizes="(max-width: 639px) 90vw, (max-width: 1023px) 45vw, 320px"
          className="absolute inset-0 z-[1] h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = '/default-teacher.jpg';
          }}
        />
        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-green-950/55 via-transparent to-transparent" />

        <div className="absolute start-3 top-3 z-[2] flex max-w-[85%] flex-wrap items-center gap-1.5">
          <span className="truncate rounded-full bg-gold-500 px-2.5 py-1 text-[11px] font-bold text-green-950 shadow-sm">
            {teacher.speciality}
          </span>
          {typeof teacher.extraSubjectsCount === 'number' && teacher.extraSubjectsCount > 0 && (
            <span className="rounded-full border border-white/40 bg-white/95 px-2 py-1 text-[11px] font-semibold text-green-800 shadow-sm">
              +{teacher.extraSubjectsCount} مواد
            </span>
          )}
        </div>
      </div>

      <div className={`flex flex-1 flex-col border-t-4 border-t-gold-500 bg-white p-4 sm:p-5`}>
        <div className="space-y-2">
          <h3
            id={titleId}
            className={`line-clamp-2 text-base font-bold leading-relaxed sm:text-lg ${cardTheme.title}`}
            title={teacher.name}
          >
            {teacher.name}
          </h3>
          <p className={`line-clamp-3 text-sm leading-relaxed ${cardTheme.subtitle}`}>{bio}</p>
          <div className={`mt-1 h-0.5 w-10 ${cardTheme.divider}`} />
        </div>

        <div className="mt-auto pt-4">
          <Link
            to={href}
            onClick={(e) => e.stopPropagation()}
            aria-label={`شوف كورسات ${teacher.name}`}
            className={`inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm ${cardTheme.btnPrimary}`}
          >
            <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
            شوف الكورسات
          </Link>
        </div>
      </div>
    </article>
  );
};

export default TeacherCard;
